import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Grid } from '@mui/material'
import { isArray } from 'lodash';
import React, { useEffect } from 'react'
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilState } from 'recoil';
import { StaticDataService } from '../../../Component/Api/StaticData/StaticDataService';
import { VerificationAgencyClientService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService';
import AppAutoComplete from '../../../Component/Common/Components/FormValidationInputs/AppAutoComplete';
import AppSelectList from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { MDTypography } from '../../../Component/Common/Components/MDTypography'
import { companyTypeList } from '../../../Component/Common/Constants/Lists/DropdownListOptions';
import { AddressResponse } from '../../../Component/Model/Dto/Address';
import { Province, Suburb } from '../../../Component/Model/Dto/StaticData';
import { VerificationAgencyClient } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClient';
import { MapAddressDtoOutToIn } from '../../../Component/Model/helpers';
import { VerificationCompanyAtom } from '../../../State/VerificationCompany.Atom';
import { CompanyDetailsValidationSchema } from './CompanyDetailsValidationSchema';

export const CompanyDetails = () => {

    const [verificationCompany, setVerificationCompany] = useRecoilState(VerificationCompanyAtom);
    const [loading, setLoading] = React.useState(false);
    const [suburbs, setSuburbs] = React.useState<Suburb[]>([]);
    const [provinces, setProvinces] = React.useState<Province[]>([]);

    const { control, handleSubmit, formState: { isSubmitting }, reset } = useForm({
        mode: 'all',
        resolver: yupResolver(CompanyDetailsValidationSchema),
    });

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const address = {
                AddressLine1: data.addressLine1,
                AddressLine2: data.addressLine2,
                Province: data.province,
                Suburb: data.suburb,
                Code: data.code,
            } as AddressResponse;

            const mappedAddress = MapAddressDtoOutToIn(address);

            if (verificationCompany.vaClientId === 0) {
                const requestData = {
                    companyName: data.companyName,
                    companyTypeId: data.companyTypeId,
                    address: mappedAddress,
                    referenceNumber: data.referenceNumber,
                    registrationNumber: data.registrationNumber,
                    tradingAs: data.tradingAs,
                    vatNumber: data.vatNumber
                } as VerificationAgencyClient.CompanyDetailsCreateDtoIn;

                const response = await VerificationAgencyClientService.CreateEntry(requestData);
                setVerificationCompany({ ...verificationCompany, vaClientId: response.data.vaClientId });
            }
            else {
                const requestData = {
                    vaClientId: verificationCompany.vaClientId,
                    companyName: data.companyName,
                    companyTypeId: data.companyTypeId,
                    address: mappedAddress,
                    referenceNumber: data.referenceNumber,
                    registrationNumber: data.registrationNumber,
                    tradingAs: data.tradingAs,
                    vatNumber: data.vatNumber
                } as VerificationAgencyClient.CompanyDetailsUpdateDtoIn;

                const response = await VerificationAgencyClientService.UpdateEntry(requestData);

            }

            toast.success("Company Details Saved")
        } catch (error: any) {
            console.log(error);
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    const getSuburbs = async () => {
        try {
            const suburbsResponse = await StaticDataService.getSuburbs();
            setSuburbs(suburbsResponse.data);
        } catch (error) {
            console.log(error);
        }
    }

    const getProvinces = async () => {
        try {
            const provincesResponse = await StaticDataService.getProvinces();
            setProvinces(provincesResponse.data);
        } catch (error) {
            console.log(error);
        }
    }

    const getCompanyDetails = async () => {
        const id = verificationCompany.vaClientId;
        if (id !== 0) {
            try {
                const response = await VerificationAgencyClientService.GetById(id);

                const formData = {
                    referenceNumber: response.data.referenceNumber,
                    companyName: response.data.companyName,
                    tradingAs: response.data.tradingAs,
                    vatNumber: response.data.vatNumber,
                    companyTypeId: response.data.companyTypeId,
                    registrationNumber: response.data.registrationNumber,
                    addressLine1: response.data.address?.addressLine1,
                    addressLine2: response.data.address?.addressLine2,
                    suburb: response.data.address?.suburb_Suburb ?
                        {
                            Id: response.data.address?.suburb_Suburb?.id,
                            Name: response.data.address?.suburb_Suburb?.name
                        } : null,
                    province: response.data.address?.province_Province ?
                        {
                            Id: response.data.address?.province_Province?.id,
                            Name: response.data.address?.province_Province?.name
                        } : null,
                    code: response.data.address?.code
                };
                reset(formData);
            } catch (error) {
                console.log(error);
            }
        }
    }

    useEffect(() => {
        setLoading(true);
        getSuburbs();
        getProvinces();
        getCompanyDetails();
        setLoading(false);
    }, [])


    return (
        <Box mt={2}>
            <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }} >
                Company Details
            </MDTypography>
            <form id='CompanyDetailsForm' onSubmit={handleSubmit(handleDataSubmit)}>
                <Grid container spacing={3}>
                    <Grid item xs={6}>
                        <Box>
                            <AppTextInput control={control} name="referenceNumber" label="Reference Number (Internal)" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="companyName" label="Company Name" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="tradingAs" label="Trading As" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppSelectList control={control} name="companyTypeId" label="Entity Type" items={companyTypeList} size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="registrationNumber" label="Registration Number" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="vatNumber" label="VAT Number" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <LoadingButton loading={isSubmitting} variant='contained' form='CompanyDetailsForm' type="submit" color='primary'>
                                Save
                            </LoadingButton>
                        </Box>
                    </Grid>
                    <Grid item xs={6}>
                        <Box>
                            <AppTextInput control={control} name="addressLine1" label="Address Line 1" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="addressLine2" label="Address Line 2" size={"small"} />
                        </Box>
                        <Box mt={2}>
                            <AppAutoComplete control={control} name="suburb" label="Suburb" options={suburbs} size={"small"} loading={loading} />
                        </Box>
                        <Box mt={2}>
                            <AppAutoComplete control={control} name="province" label="Province" options={provinces} size={"small"} loading={loading} />
                        </Box>
                        <Box mt={2}>
                            <AppTextInput control={control} name="code" label="Code" size={"small"} />
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    )
}
